/**
 * The home page (/dashboard): live announcements and the widgets this person
 * has added, on a two-column grid.
 *
 * The Inventory Audit and Website Audit count cards — and the Refresh bar that
 * only existed to refresh them — came off on 2026-09-28 (Ian: "I dont want it
 * on the homepage by default"). Widgets are the way back, saved per user as
 * one preference. One exception to "nothing by default", ruled 2026-10-09:
 * a person who has never saved a widget list sees the large "Not on
 * <their site>" widget, so every existing and future account starts with it;
 * the moment they edit — even to remove it — their own list is what shows.
 * Both still need the audit permission and a configured site.
 *
 * Editing is a mode (Ian, 2026-10-09: the size and Remove controls "stay on
 * the page … there needs to be a feature where they can edit widget and then
 * hit save so the edits text goes away"). "Edit widgets" reveals the controls
 * on each card and the dashed "Add widget" slot where the next widget would
 * land; "Save" hides them. Each change is saved as it is made, so nothing is
 * lost if the person navigates away mid-edit; Save only ends the editing. A
 * home page with no widgets yet shows the slot without entering edit mode, so
 * the first widget is one click away.
 *
 * A medium widget takes one column and a large one takes both, so two mediums
 * side by side are the width of one large. "Add widget" offers one entry per
 * monitored website, by name — "Not on dolanautogroup.com" — because a second
 * site such as dolanrenotoyota.com will be its own widget (Ian, 2026-10-09).
 */
import React, { useEffect, useState } from "react"

import AnnouncementBar from "../components/AnnouncementBar"
import Layout from "../components/Layout"
import NotOnWebsiteWidget from "../components/home/NotOnWebsiteWidget"
import {
  HOME_WIDGETS_PREFERENCE,
  HomeWidget,
  WidgetSize,
  parseHomeWidgets,
  sameWidget,
  serializeHomeWidgets,
} from "../components/home/homeWidgets"
import { useAuth } from "../context/AuthContext"
import { useDealerGroup } from "../context/DealerGroupContext"
import { useIsMobile } from "../hooks/useIsMobile"
import { getNotOnWebsiteSummary } from "../api/notOnWebsite"
import { getPreferences, updatePreference } from "../api/preferences"
import { THEME } from "../config/theme"

const VIEW_AUDIT_PERMISSION = "view_inventory_audit"

const HomePage: React.FC = () => (
  <Layout>
    <HomeContent />
  </Layout>
)

export default HomePage

/** Exported for tests, which render it without the app shell. */
export function HomeContent() {
  const { hasPermission } = useAuth()
  const { dealerGroupId, websiteDomain } = useDealerGroup()
  const isMobile = useIsMobile()
  // What the person has saved: undefined while loading; null when they have
  // never saved a widget list, which is when the default applies. A saved
  // list — even an empty one, after a Remove — is theirs and is respected.
  const [saved, setSaved] = useState<HomeWidget[] | null | undefined>(undefined)
  const [editing, setEditing] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  // The sites this group monitors, by name, for the "Add widget" entries.
  // Today a group has one configured site; each site is its own widget. The
  // group switcher names it from the first paint; the summary is the fallback
  // for a group record that carries none.
  const [fetchedSites, setFetchedSites] = useState<string[]>([])
  const sites = websiteDomain ? [websiteDomain] : fetchedSites

  useEffect(() => {
    let cancelled = false
    getPreferences()
      .then((preferences) => {
        if (cancelled) return
        const raw = preferences[HOME_WIDGETS_PREFERENCE]
        setSaved(raw === undefined ? null : parseHomeWidgets(raw))
      })
      .catch(() => {
        if (!cancelled) setSaved(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const canAddAuditWidgets = hasPermission(VIEW_AUDIT_PERMISSION)
  useEffect(() => {
    if (!canAddAuditWidgets || dealerGroupId === null || websiteDomain) {
      setFetchedSites([])
      return undefined
    }
    let cancelled = false
    getNotOnWebsiteSummary(dealerGroupId)
      .then((summary) => {
        if (!cancelled) setFetchedSites(summary.website_domain ? [summary.website_domain] : [])
      })
      .catch(() => {
        if (!cancelled) setFetchedSites([])
      })
    return () => {
      cancelled = true
    }
  }, [canAddAuditWidgets, dealerGroupId, websiteDomain])

  // What "Add widget" can offer: one Not-on-the-site card per monitored site.
  const catalogue: Array<{ widget: HomeWidget; label: string }> = canAddAuditWidgets
    ? (sites.length > 0 ? sites : [null]).map((site) => ({
        widget: { type: "not_on_website", size: "medium", site },
        label: `Not on ${site ?? "the website"}`,
      }))
    : []
  // The default for someone who has never saved a list: the large card for
  // each monitored site (Ian, 2026-10-09: every account gets "Not on
  // dolanautogroup.com" at the large size unless they change it). It needs a
  // configured site to be named after; without one there is no default.
  const defaultWidgets: HomeWidget[] = sites.length > 0
    ? catalogue.map(({ widget }) => ({ ...widget, size: "large" as const }))
    : []
  const widgets: HomeWidget[] | undefined = saved === undefined ? undefined : saved ?? defaultWidgets

  const save = (next: HomeWidget[]) => {
    setSaved(next)
    // Saved per user as each change is made; a failed save keeps the page
    // usable and the next change tries again.
    void updatePreference(HOME_WIDGETS_PREFERENCE, serializeHomeWidgets(next))
  }
  const addWidget = (widget: HomeWidget) => {
    setMenuOpen(false)
    if (widgets?.some((existing) => sameWidget(existing, widget))) return
    save([...(widgets ?? []), widget])
  }
  const resizeWidget = (target: HomeWidget, size: WidgetSize) =>
    save((widgets ?? []).map((widget) => (sameWidget(widget, target) ? { ...widget, size } : widget)))
  const removeWidget = (target: HomeWidget) =>
    save((widgets ?? []).filter((widget) => !sameWidget(widget, target)))

  const hasWidgets = (widgets?.length ?? 0) > 0
  const showSlot = catalogue.length > 0 && (editing || !hasWidgets)

  return (
    <>
      {/* Announcement bar(s) — global + this user's dealer group, live now.
          Renders nothing when there are none. */}
      <AnnouncementBar />

      {widgets !== undefined && (
        <>
          {/* The page's title row, like the audit page's: the name at the left,
              the page-level action at the right, so the button is not a stray. */}
          <div style={titleRowStyle}>
            <h1 style={titleStyle}>Home</h1>
            {hasWidgets && catalogue.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  setEditing((on) => !on)
                }}
                style={editing ? saveButtonStyle : editButtonStyle}
              >
                {editing ? "Save" : "Edit widgets"}
              </button>
            )}
          </div>

          <div style={gridStyle(isMobile, true)}>
            {widgets.map((widget) =>
              widget.type === "not_on_website" ? (
                <div
                  key={`${widget.type}:${widget.site ?? ""}`}
                  style={{ gridColumn: widget.size === "large" && !isMobile ? "1 / -1" : "auto" }}
                >
                  <NotOnWebsiteWidget
                    size={widget.size}
                    editing={editing}
                    onResize={(size) => resizeWidget(widget, size)}
                    onRemove={() => removeWidget(widget)}
                  />
                </div>
              ) : null
            )}

            {showSlot && (
              <div style={{ position: "relative" }}>
                {/* The next widget's place: a dashed outline with the call in the middle. */}
                <button
                  type="button"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((open) => !open)}
                  style={slotStyle}
                >
                  <span style={{ fontSize: 22, lineHeight: 1, color: THEME.colors.navy }} aria-hidden="true">+</span>
                  <span>Add widget</span>
                </button>
                {menuOpen && (
                  <div role="menu" aria-label="Widgets" style={menuStyle}>
                    {catalogue.map(({ widget, label }) => {
                      const added = (widgets ?? []).some((existing) => sameWidget(existing, widget))
                      return (
                        <button
                          key={label}
                          type="button"
                          role="menuitem"
                          disabled={added}
                          onClick={() => addWidget(widget)}
                          style={menuItemStyle(added)}
                        >
                          <span>{label}</span>
                          <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
                            {added ? "added" : "add"}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}

function gridStyle(isMobile: boolean, belowToolbar: boolean): React.CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
    gap: 18,
    marginTop: belowToolbar ? "12px" : "24px",
    alignItems: "stretch",
  }
}

const titleRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  marginTop: "24px",
}

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 24,
  fontWeight: 500,
  letterSpacing: "-0.5px",
  color: THEME.colors.neutral.light.textPrimary,
}

const editButtonStyle: React.CSSProperties = {
  padding: "7px 14px",
  fontSize: 12.5,
  fontFamily: THEME.fonts.primary,
  fontWeight: 600,
  borderRadius: THEME.borderRadius.sm,
  border: `1px solid ${THEME.colors.navy}`,
  background: THEME.colors.white,
  color: THEME.colors.navy,
  cursor: "pointer",
}

const saveButtonStyle: React.CSSProperties = {
  ...editButtonStyle,
  background: THEME.colors.navy,
  color: THEME.colors.white,
}

const slotStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 180,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  borderRadius: THEME.borderRadius.lg,
  border: `2px dashed ${THEME.colors.silver}`,
  background: "transparent",
  color: THEME.colors.navy,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
}

const menuStyle: React.CSSProperties = {
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, 24px)",
  minWidth: 260,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.neutral.light.border}`,
  borderRadius: THEME.borderRadius.md,
  boxShadow: THEME.modal.shadow,
  overflow: "hidden",
  zIndex: 2,
}

function menuItemStyle(added: boolean): React.CSSProperties {
  return {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    width: "100%",
    padding: "9px 12px",
    fontSize: 13,
    fontFamily: THEME.fonts.primary,
    textAlign: "left",
    background: "none",
    border: "none",
    borderTop: `1px solid ${THEME.colors.neutral.light.border}`,
    color: THEME.colors.neutral.light.textPrimary,
    cursor: added ? "default" : "pointer",
    opacity: added ? 0.7 : 1,
  }
}
